import config from './config';
import { portalsFetch, readErrorMessage } from './portalsFetch';
import {
  FluviusEan,
  FluviusMandatesResponse,
  CreateMandateLinkRequest,
  CreateMandateLinkResponse,
  SendMandateLinkRequest,
  SendMandateLinkResponse,
} from '../types/mandateTypes';

export type { FluviusEan };

/**
 * Non-2xx answer from one of the Fluvius Data Manager endpoints. `status` is the HTTP status;
 * `backendMessage` is the message the backend put in the body (JSON `error`/`message` or a
 * plain-text body), when there was one — safe to show, unlike `message`, which falls back
 * to a technical "Failed to …: <status>" string.
 */
export class FluviusMandatesError extends Error {
  status: number;
  backendMessage?: string;

  constructor(status: number, message: string, backendMessage?: string) {
    super(message);
    this.name = 'FluviusMandatesError';
    this.status = status;
    this.backendMessage = backendMessage;
  }
}

/**
 * Thrown when POST /fluvius/mandates/send-link answers 500 because EVERY recipient
 * failed. The backend still sends the full { sent: 0, failed: N, results: [...] } body
 * in that case, so this carries the per-recipient detail instead of collapsing it into
 * a bare "500" message. Extends Error, so callers that only read `.message` are
 * unaffected.
 */
export class SendMandateLinkAllFailedError extends Error {
  response: SendMandateLinkResponse;

  constructor(response: SendMandateLinkResponse) {
    super(`Failed to send mandate link: all ${response.failed} recipient(s) failed`);
    this.name = 'SendMandateLinkAllFailedError';
    this.response = response;
  }
}

const isSendMandateLinkResponse = (data: unknown): data is SendMandateLinkResponse =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as SendMandateLinkResponse).sent === 'number' &&
  typeof (data as SendMandateLinkResponse).failed === 'number' &&
  Array.isArray((data as SendMandateLinkResponse).results);

export const fetchFluviusMandates = async (companyId: number): Promise<FluviusMandatesResponse> => {
  const url = `${config.API_SERVICES}/api/v1/fluvius/mandates?companyId=${companyId}`;

  const response = await portalsFetch(url);

  if (!response.ok) {
    const backendMessage = await readErrorMessage(response);
    throw new FluviusMandatesError(
      response.status,
      backendMessage || `Failed to fetch Fluvius mandates: ${response.status}`,
      backendMessage
    );
  }

  return response.json();
};

export const createMandateLink = async (body: CreateMandateLinkRequest): Promise<CreateMandateLinkResponse> => {
  const url = `${config.API_SERVICES}/api/v1/fluvius/mandates/link`;

  const response = await portalsFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const backendMessage = await readErrorMessage(response);
    throw new FluviusMandatesError(
      response.status,
      backendMessage || `Failed to create mandate link: ${response.status}`,
      backendMessage
    );
  }

  return response.json();
};

export const sendMandateLink = async (body: SendMandateLinkRequest): Promise<SendMandateLinkResponse> => {
  const url = `${config.API_SERVICES}/api/v1/fluvius/mandates/send-link`;

  const response = await portalsFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    // readErrorMessage reads a clone, so the body is still available for the shape check.
    const backendMessage = await readErrorMessage(response);
    const data: unknown = await response.json().catch(() => null);
    // An all-recipients-failed 500 carries the same { sent, failed, results } body as a
    // 200 (see fluviusHandler.sendMandateLink). Preserve it so the UI can name the
    // addresses that bounced; a generic 500 ({ error: ... }) still throws plainly.
    if (isSendMandateLinkResponse(data)) {
      throw new SendMandateLinkAllFailedError(data);
    }
    throw new FluviusMandatesError(
      response.status,
      backendMessage || `Failed to send mandate link: ${response.status}`,
      backendMessage
    );
  }

  return response.json();
};
